/**
 * Tiny localStorage helpers for "did this happen recently?" flags. Each
 * flag stores a write timestamp; `isFresh` returns false (and clears the
 * key) once the TTL has elapsed.
 *
 * Use for one-shot UX gates (do-not-nag-for-30-days, resume-flow-after-
 * install-within-7-days). Not for state that must survive backend trips.
 * This is browser-local only.
 */

const safeRead = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeWrite = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {}
};

const safeRemove = (key: string) => {
  try {
    window.localStorage.removeItem(key);
  } catch {}
};

/** Stamp the current timestamp under `key`. */
export const setFlag = (key: string) => safeWrite(key, String(Date.now()));

/** Remove the flag at `key`. */
export const clearFlag = (key: string) => safeRemove(key);

/** Whether `key` was set within the last `ttlMs`. Clears the key if expired. */
export const isFlagFresh = (key: string, ttlMs: number): boolean => {
  const raw = safeRead(key);
  if (!raw) return false;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return false;
  if (Date.now() - ts > ttlMs) {
    safeRemove(key);
    return false;
  }
  return true;
};
